import { z } from 'zod';
import { IsoDate, IsoDateTime, MAX_PENCE, Pence, Uuid } from '../primitives.js';

/**
 * WS7 phase 2 — booking-policy engine contract: the money rules every salon configures once
 * (deposits, cancellation windows, no-show fees) and the client is shown BEFORE they commit.
 *
 * Design pillars (FOUNDATION + brief 07):
 *  • Honesty brand position — the policy text a client sees is generated from the SAME structured
 *    rule that later gets enforced, so they always saw exactly what gets charged. Plain English,
 *    UK, no asterisks.
 *  • Opt-in automation — auto-charging late-cancel / no-show fees is OFF by default (`armAutoCharge`).
 *    Default behaviour is "suggest, staff confirm"; we never take money on automation the salon
 *    didn't arm. Same brand logic as comms (we pause, never auto-spend).
 *  • Immutability of terms — editing a policy NEVER retroactively changes an already-booked
 *    appointment. Each PUT snapshots an immutable `policy_version`; an appointment references the
 *    version it was shown (appointments.booking_policy_version_id), so enforcement reads that
 *    frozen snapshot, not the live policy.
 *
 * Three tables back this contract (all salon-scoped, RLS):
 *  • booking_policies            — the per-salon default rule set (one row per salon).
 *  • service_policy_overrides    — per-service overrides keyed to services (sparse; only the fields
 *                                  a salon wants to differ from the default).
 *  • policy_versions             — append-only immutable snapshots (text + structured rule), one per
 *                                  edit; the appointment links to the version it displayed.
 */

// ── Deposit rule ───────────────────────────────────────────────────────────────

/** How a deposit amount is computed when one is required. */
export const DepositKind = z.enum(['none', 'fixed', 'percent']);
export type DepositKind = z.infer<typeof DepositKind>;

/**
 * When a deposit is required. `always` and `never` are unconditional; the two predicates layer on
 * top of `always` so a salon can say "deposit only from new clients" or "deposit only on services
 * priced over £X". Both predicates may be combined (require when new-client OR over-threshold).
 */
export const DepositWhen = z.enum(['always', 'never', 'new_clients', 'over_threshold']);
export type DepositWhen = z.infer<typeof DepositWhen>;

/**
 * The deposit rule. `kind` decides the maths; `when` decides whether it applies to a given
 * booking. A `percent` deposit is a percent-of-price (basis points kept as a 0–100 integer for
 * plain-English fidelity); a `fixed` deposit is integer pence.
 */
export const DepositRule = z
  .object({
    kind: DepositKind.default('none'),
    /** Used when kind = 'fixed'. Integer pence. */
    amountPence: Pence.default(0),
    /** Used when kind = 'percent'. Whole-percent of the service price (0–100). */
    percent: z.int().min(0).max(100).default(0),
    when: DepositWhen.default('always'),
    /**
     * Used when `when = 'over_threshold'`: only require a deposit on services priced at or above
     * this many pence. A "services over £50" rule sets this to 5000.
     */
    overThresholdPence: Pence.default(0),
  })
  .meta({ id: 'DepositRule' });
export type DepositRule = z.infer<typeof DepositRule>;

// ── Cancellation rule ──────────────────────────────────────────────────────────

/** What a late-cancellation costs once the free window has closed. */
export const CancellationFeeKind = z.enum(['none', 'forfeit_deposit', 'fixed', 'percent']);
export type CancellationFeeKind = z.infer<typeof CancellationFeeKind>;

/**
 * Free until N hours before the appointment; cancelling inside that window incurs `feeKind`.
 * `forfeit_deposit` keeps the deposit already paid; `fixed` is integer pence; `percent` is a
 * whole-percent of the quoted price.
 */
export const CancellationRule = z
  .object({
    freeUntilHours: z.int().min(0).max(8760).default(24).describe('free cancellation lead time'),
    feeKind: CancellationFeeKind.default('none'),
    /** Used when feeKind = 'fixed'. Integer pence. */
    amountPence: Pence.default(0),
    /** Used when feeKind = 'percent'. Whole-percent of the quoted price (0–100). */
    percent: z.int().min(0).max(100).default(0),
  })
  .meta({ id: 'CancellationRule' });
export type CancellationRule = z.infer<typeof CancellationRule>;

// ── No-show rule ────────────────────────────────────────────────────────────────

/** What a no-show costs. */
export const NoShowFeeKind = z.enum(['none', 'forfeit_deposit', 'fixed', 'percent']);
export type NoShowFeeKind = z.infer<typeof NoShowFeeKind>;

export const NoShowRule = z
  .object({
    feeKind: NoShowFeeKind.default('none'),
    /** Used when feeKind = 'fixed'. Integer pence. */
    amountPence: Pence.default(0),
    /** Used when feeKind = 'percent'. Whole-percent of the quoted price (0–100). */
    percent: z.int().min(0).max(100).default(0),
  })
  .meta({ id: 'NoShowRule' });
export type NoShowRule = z.infer<typeof NoShowRule>;

// ── The composed rule set ────────────────────────────────────────────────────────

/**
 * The full structured rule for a salon (or, in an override, the per-service deltas). This is the
 * object that `renderPolicyText` turns into plain English and that gets frozen into a
 * `policy_version` on every edit.
 */
export const PolicyRule = z
  .object({
    deposit: DepositRule,
    cancellation: CancellationRule,
    noShow: NoShowRule,
    /**
     * Opt-in: when true, the salon has armed auto-charging of late-cancel / no-show fees. Default
     * FALSE — the engine only SUGGESTS a fee and staff confirm. Persisted on the policy so it is
     * frozen into each version too (an auto-charge always references the version the client saw).
     */
    armAutoCharge: z.boolean().default(false),
  })
  .meta({ id: 'PolicyRule' });
export type PolicyRule = z.infer<typeof PolicyRule>;

// ── Salon policy (the live, editable record) ─────────────────────────────────────

export const BookingPolicy = z
  .object({
    salonId: Uuid,
    rule: PolicyRule,
    /** The version the CURRENT live rule was snapshotted into (the latest policy_version). */
    currentVersionId: Uuid,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'BookingPolicy' });
export type BookingPolicy = z.infer<typeof BookingPolicy>;

/**
 * Replace the salon's policy. A PUT creates a NEW immutable policy_version and points the live
 * policy at it. Omitted sub-rules fall back to their schema defaults (a full replace, not a merge),
 * matching the "configure it once, see exactly what you set" model.
 */
export const SetBookingPolicy = z
  .object({
    deposit: DepositRule.optional(),
    cancellation: CancellationRule.optional(),
    noShow: NoShowRule.optional(),
    armAutoCharge: z.boolean().optional(),
  })
  .meta({ id: 'SetBookingPolicy' });
export type SetBookingPolicy = z.infer<typeof SetBookingPolicy>;

// ── Per-service override ─────────────────────────────────────────────────────────

/**
 * A per-service override. Each sub-rule is OPTIONAL: a null/absent sub-rule means "fall back to the
 * salon default for this aspect". This lets a salon say "this £20 fringe trim takes no deposit"
 * without restating the whole policy.
 */
export const ServicePolicyOverride = z
  .object({
    id: Uuid,
    salonId: Uuid,
    serviceId: Uuid,
    deposit: DepositRule.nullable(),
    cancellation: CancellationRule.nullable(),
    noShow: NoShowRule.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'ServicePolicyOverride' });
export type ServicePolicyOverride = z.infer<typeof ServicePolicyOverride>;

export const SetServicePolicyOverride = z
  .object({
    deposit: DepositRule.nullish(),
    cancellation: CancellationRule.nullish(),
    noShow: NoShowRule.nullish(),
  })
  .meta({ id: 'SetServicePolicyOverride' });
export type SetServicePolicyOverride = z.infer<typeof SetServicePolicyOverride>;

// ── Immutable policy version (the frozen snapshot) ───────────────────────────────

/**
 * An append-only snapshot of a salon's policy at the moment a booking was shown its terms. The
 * appointment references the version id; enforcement reads THIS frozen rule, never the live policy,
 * so editing the policy can never retroactively change an already-booked appointment's terms.
 */
export const PolicyVersion = z
  .object({
    id: Uuid,
    salonId: Uuid,
    rule: PolicyRule.describe('the structured rule frozen at snapshot time'),
    text: z.string().describe('the plain-English rendering shown to the client'),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'PolicyVersion' });
export type PolicyVersion = z.infer<typeof PolicyVersion>;

// ── Resolved policy text (read by every booking surface) ─────────────────────────

/**
 * The resolved, plain-English policy for a given service: the salon default with any per-service
 * override folded in, rendered to text, plus the policy_version id the booking surface must persist
 * onto the appointment it creates so enforcement later reads the same frozen terms.
 */
export const ResolvedServicePolicy = z
  .object({
    serviceId: Uuid,
    policyVersionId: Uuid.describe('persist onto the appointment at booking time'),
    rule: PolicyRule.describe('the effective rule after folding the override over the default'),
    text: z.string().describe('plain-English terms to show the client'),
    depositText: z.string(),
    cancellationText: z.string(),
    noShowText: z.string(),
  })
  .meta({ id: 'ResolvedServicePolicy' });
export type ResolvedServicePolicy = z.infer<typeof ResolvedServicePolicy>;

// ── Pure-function I/O contracts (resolveDepositRequirement / computeCancellationOutcome) ──

/**
 * The minimal service shape the deposit/fee maths needs: its price drives percent-of-price
 * deposits/fees and the over-threshold predicate. (Decoupled from the full Service entity so the
 * pure functions stay trivially unit-testable.)
 */
export const PolicyServiceInput = z.object({
  id: Uuid,
  priceFromPence: Pence,
});
export type PolicyServiceInput = z.infer<typeof PolicyServiceInput>;

/** The client facts the predicates need. A null client is treated as a NEW client (anonymous). */
export const PolicyClientInput = z.object({
  id: Uuid.nullable(),
  visitCount: z.int().nonnegative().default(0).describe('completed past visits; 0 ⇒ new client'),
});
export type PolicyClientInput = z.infer<typeof PolicyClientInput>;

/** The appointment facts the maths needs (price quoted, deposit already paid, when it starts). */
export const PolicyApptInput = z.object({
  priceQuotedPence: Pence.default(0),
  depositPaidPence: Pence.default(0),
  startAt: IsoDateTime,
});
export type PolicyApptInput = z.infer<typeof PolicyApptInput>;

/** Result of resolveDepositRequirement. */
export const DepositRequirement = z
  .object({
    required: z.boolean(),
    amountPence: z.int().min(0).max(MAX_PENCE),
    reason: z
      .enum(['not_required', 'always', 'new_client', 'over_threshold'])
      .describe('why a deposit is (not) required — drives the suggestion copy'),
    policyVersionId: Uuid,
  })
  .meta({ id: 'DepositRequirement' });
export type DepositRequirement = z.infer<typeof DepositRequirement>;

/** Result of computeCancellationOutcome. */
export const CancellationOutcome = z
  .object({
    withinFreeWindow: z.boolean(),
    feeKind: CancellationFeeKind,
    amountPence: z.int().min(0).max(MAX_PENCE),
    /** True only when the salon armed auto-charge AND a fee is due — the gate Integrate checks. */
    autoCharge: z.boolean(),
    policyVersionId: Uuid,
  })
  .meta({ id: 'CancellationOutcome' });
export type CancellationOutcome = z.infer<typeof CancellationOutcome>;

/** Result of computeNoShowOutcome (mirrors the cancellation outcome; no free window). */
export const NoShowOutcome = z
  .object({
    feeKind: NoShowFeeKind,
    amountPence: z.int().min(0).max(MAX_PENCE),
    autoCharge: z.boolean(),
    policyVersionId: Uuid,
  })
  .meta({ id: 'NoShowOutcome' });
export type NoShowOutcome = z.infer<typeof NoShowOutcome>;

/** Query for the resolved policy text of a service. */
export const ResolvedPolicyQuery = z.object({
  serviceId: Uuid,
  /** Optional: tailor the deposit predicate (new-client text) to a specific client. */
  clientId: Uuid.optional(),
  /** Optional: an ISO date the booking is for (unused by the maths; reserved for surfaces). */
  date: IsoDate.optional(),
});
export type ResolvedPolicyQuery = z.infer<typeof ResolvedPolicyQuery>;
